import React, { useState, useEffect } from "react";
import { MoonLoader } from "react-spinners";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import DescriptionIcon from "@mui/icons-material/Description";
import "./FilePreviewModal.css";

const FilePreviewModal = ({
  attachment,
  isOpen,
  onClose,
  baseURL = "",
  getFileUrl = null,
}) => {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Handle close with escape key and prevent background scrolling
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    // Prevent common download shortcuts and actions
    const preventDownload = (e) => {
      // Prevent Ctrl+S, Ctrl+Shift+S, Ctrl+P, F12, etc.
      if (
        (e.ctrlKey &&
          (e.key === "s" || e.key === "S" || e.key === "p" || e.key === "P")) ||
        (e.ctrlKey && e.shiftKey && (e.key === "s" || e.key === "S")) ||
        e.key === "F12" ||
        (e.ctrlKey && e.key === "u") || // View source
        (e.ctrlKey && e.key === "i") || // Developer tools
        (e.ctrlKey && e.key === "j") // Developer tools
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Prevent right-click context menu
    const preventContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Prevent drag and drop
    const preventDragDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("keydown", preventDownload, true);
      document.addEventListener("contextmenu", preventContextMenu, true);
      document.addEventListener("dragstart", preventDragDrop, true);
      document.addEventListener("drop", preventDragDrop, true);
      document.addEventListener("dragover", preventDragDrop, true);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
      document.body.style.userSelect = "none"; // Prevent text selection
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", preventDownload, true);
      document.removeEventListener("contextmenu", preventContextMenu, true);
      document.removeEventListener("dragstart", preventDragDrop, true);
      document.removeEventListener("drop", preventDragDrop, true);
      document.removeEventListener("dragover", preventDragDrop, true);
      document.body.style.overflow = "unset";
      document.body.style.userSelect = "auto";
    };
  }, [isOpen, onClose]);

  // Handle loading state when modal opens
  useEffect(() => {
    if (isOpen && attachment) {
      setPreviewLoading(true);
      setPreviewError(null);

      // Simulate loading time for better UX
      const timer = setTimeout(() => {
        setPreviewLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen, attachment]);

  if (!isOpen || !attachment) {
    return null;
  }

  // Get file URL
  const getSecurePreviewUrl = () => {
    if (getFileUrl && typeof getFileUrl === "function") {
      return getFileUrl(attachment);
    }
    return `${baseURL}/${attachment.filePath || attachment.file}`;
  };

  // Determine file type and get appropriate icon
  const getFileTypeInfo = () => {
    const fileName = attachment.name || "";
    const fileExtension = fileName.split(".").pop()?.toLowerCase();

    const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
    const pdfTypes = ["pdf"];
    const powerpointTypes = ["ppt", "pptx", "pps", "ppsx"];
    const wordTypes = ["doc", "docx"];

    if (imageTypes.includes(fileExtension)) {
      return { type: "image", icon: <ImageIcon />, supported: true };
    } else if (pdfTypes.includes(fileExtension)) {
      return { type: "pdf", icon: <PictureAsPdfIcon />, supported: true };
    } else if (powerpointTypes.includes(fileExtension)) {
      return { type: "powerpoint", icon: <SlideshowIcon />, supported: true };
    } else if (wordTypes.includes(fileExtension)) {
      return { type: "word", icon: <DescriptionIcon />, supported: true };
    } else {
      return {
        type: "unsupported",
        icon: <AttachFileIcon />,
        supported: false,
      };
    }
  };

  const fileTypeInfo = getFileTypeInfo();

  const renderPreviewContent = () => {
    if (previewLoading) {
      return (
        <div className="previewLoading">
          <MoonLoader color="#012369" size={40} />
          <p>Loading preview...</p>
        </div>
      );
    }

    if (previewError) {
      return (
        <div className="previewError">
          <AttachFileIcon style={{ fontSize: "48px", color: "#ef4444" }} />
          <p>Failed to load preview</p>
          <p className="errorDetails">{previewError}</p>
        </div>
      );
    }

    switch (fileTypeInfo.type) {
      case "image":
        return (
          <>
            {attachment?.filePath ? (
              <img
                src={getSecurePreviewUrl()}
                alt={attachment.name}
                className="previewImage"
                onContextMenu={(e) => e.preventDefault()} // Disable right-click
                onDragStart={(e) => e.preventDefault()} // Disable drag
                onLoad={() => setPreviewLoading(false)}
                onError={() => setPreviewError("Failed to load image")}
                style={{
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              />
            ) : (
              ""
            )}
          </>
        );

      case "pdf":
        return (
          <>
            {attachment?.filePath ? (
              <div className="previewPdfContainer">
                <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(
                    getSecurePreviewUrl()
                  )}&embedded=true`}
                  className="previewPdf"
                  title={attachment.name}
                  onContextMenu={(e) => e.preventDefault()} // Disable right-click
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => setPreviewError("Failed to load PDF")}
                  style={{
                    border: "none",
                    outline: "none",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    MozUserSelect: "none",
                    msUserSelect: "none",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
            ) : (
              ""
            )}
          </>
        );

      case "powerpoint":
        return (
          <div className="previewPowerPoint">
            {attachment?.filePath ? (
              <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(
                  getSecurePreviewUrl()
                )}&embedded=true`}
                className="previewPowerPointFrame"
                title={attachment.name}
                onContextMenu={(e) => e.preventDefault()} // Disable right-click
                onLoad={() => setPreviewLoading(false)}
                onError={() =>
                  setPreviewError("Failed to load PowerPoint presentation")
                }
                style={{
                  border: "none",
                  outline: "none",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                }}
              />
            ) : (
              ""
            )}
          </div>
        );

      case "word":
        return (
          <div className="previewWord">
            {attachment?.filePath ? (
              <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(
                  getSecurePreviewUrl()
                )}&embedded=true`}
                className="previewWordFrame"
                title={attachment.name}
                onContextMenu={(e) => e.preventDefault()}
                onLoad={() => setPreviewLoading(false)}
                onError={() => setPreviewError("Failed to load Word document")}
                style={{
                  border: "none",
                  outline: "none",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                }}
              />
            ) : (
              ""
            )}
          </div>
        );

      default:
        return (
          <div className="previewUnsupported">
            <AttachFileIcon style={{ fontSize: "48px", color: "#ccc" }} />
            <p>Preview not available for this file type</p>
            <p className="fileTypeInfo">
              File type:{" "}
              {attachment.name?.split(".").pop()?.toUpperCase() || "Unknown"}
            </p>
          </div>
        );
    }
  };

  return (
    <div className="previewModal">
      <div className="previewModalOverlay" onClick={onClose}></div>
      <div className="previewModalContent">
        <div className="previewModalHeader">
          <div className="fileInfo">
            <div className="fileIcon">{fileTypeInfo.icon}</div>
            <div className="fileDetails">
              <h3>{attachment.name}</h3>
              <p className="fileType">
                {fileTypeInfo.type === "unsupported"
                  ? "Unsupported file type"
                  : `${
                      fileTypeInfo.type.charAt(0).toUpperCase() +
                      fileTypeInfo.type.slice(1)
                    } file`}
              </p>
            </div>
          </div>
          <button
            className="previewCloseButton"
            onClick={onClose}
            title="Close preview (ESC)"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="previewModalBody">{renderPreviewContent()}</div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
